import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import websocketService from '../services/websocket';
import Sparkline from '../components/Sparkline';
import Navbar from '../components/Navbar';
import {
    TrendingUp,
    LogOut,
    Search,
    Star,
    Bell,
    ArrowUpRight,
    ArrowDownRight,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';

const MarketsPage = () => {
    const { user, logout } = useAuthStore();
    const [markets, setMarkets] = useState([]);
    const [sparklineData, setSparklineData] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 30;

    // 1. Initial Fetch of All Markets
    useEffect(() => {
        const fetchMarkets = async () => {
            try {
                const response = await api.get('/markets');
                setMarkets(response.data.tradingPairs);
                setLoading(false);
            } catch (error) {
                console.error('Fetch error:', error);
                toast.error('Failed to load markets');
                setLoading(false);
            }
        };

        fetchMarkets();

        const token = localStorage.getItem('token');
        if (token) {
            websocketService.connect(token);
        }

        return () => {
            websocketService.disconnect();
        };
    }, []);

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
    };

    // 2. Filter and Sort Markets
    const filteredMarkets = useMemo(() => {
        let filtered = markets;

        if (searchTerm) {
            filtered = filtered.filter(m =>
                m.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                m.baseToken?.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        switch (activeTab) {
            case 'gainers':
                return [...filtered].sort((a, b) => parseFloat(b.priceChange24h) - parseFloat(a.priceChange24h));
            case 'losers':
                return [...filtered].sort((a, b) => parseFloat(a.priceChange24h) - parseFloat(b.priceChange24h));
            case 'volume':
                return [...filtered].sort((a, b) => parseFloat(b.volume24h) - parseFloat(a.volume24h));
            case 'all':
            default:
                // Default sort by volume for 'all' to show most relevant first
                return [...filtered].sort((a, b) => parseFloat(b.volume24h) - parseFloat(a.volume24h));
        }
    }, [markets, activeTab, searchTerm]);

    // 3. Pagination Logic
    const totalPages = Math.ceil(filteredMarkets.length / itemsPerPage);
    const paginatedMarkets = useMemo(() => {
        return filteredMarkets.slice(
            (currentPage - 1) * itemsPerPage,
            currentPage * itemsPerPage
        );
    }, [filteredMarkets, currentPage]);

    // 4. Fetch Sparklines & Subscribe for Visible Markets
    useEffect(() => {
        if (paginatedMarkets.length === 0) return;

        const symbols = paginatedMarkets.map(m => m.symbol);
        const symbolString = symbols.join(',');

        // Fetch sparklines for current page only
        api.get(`/markets/sparklines?symbols=${symbolString}`)
            .then(res => {
                setSparklineData(prev => ({ ...prev, ...res.data.sparklineData }));
            })
            .catch(err => console.error('Sparkline fetch failed:', err));

        // Subscribe to WebSocket for current page
        websocketService.subscribeToMarkets(symbols);

        const handleTickerUpdate = (data) => {
            setMarkets(prevMarkets => {
                const index = prevMarkets.findIndex(m => m.symbol === data.symbol);
                if (index === -1) return prevMarkets;

                const newMarkets = [...prevMarkets];
                // Only update if price changed to avoid unnecessary re-renders
                if (newMarkets[index].lastPrice !== data.price) {
                    newMarkets[index] = {
                        ...newMarkets[index],
                        lastPrice: data.price,
                        priceChange24h: data.priceChangePercent,
                        volume24h: data.volume,
                        high24h: data.high,
                        low24h: data.low
                    };
                }
                return newMarkets;
            });
        };

        websocketService.on('ticker', handleTickerUpdate);

        return () => {
            // Optional: Unsubscribe when page changes to save bandwidth
            // websocketService.unsubscribeFromMarkets(symbols); 
            websocketService.off('ticker', handleTickerUpdate);
        };
    }, [paginatedMarkets]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm]);

    const getSparklineForSymbol = (symbol) => {
        if (sparklineData[symbol] && sparklineData[symbol].length > 0) {
            return sparklineData[symbol];
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-[#0b0e11] text-gray-100 font-sans">
            <Navbar />

            <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white mb-2">Cryptocurrency Markets</h1>
                        <p className="text-gray-400">Real-time prices and market data for all trading pairs</p>
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="flex items-center gap-2 bg-[#181a20] border border-gray-800 px-4 py-2 rounded-xl flex-1 sm:flex-initial">
                            <Search size={18} className="text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search markets..."
                                className="bg-transparent border-none focus:outline-none text-white placeholder-gray-500 w-full sm:w-64"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
                        <div className="text-gray-400 text-sm mb-1">Total Markets</div>
                        <div className="text-2xl font-bold text-white">{markets.length}</div>
                    </div>
                    <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
                        <div className="text-gray-400 text-sm mb-1">24h Volume</div>
                        <div className="text-2xl font-bold text-white">
                            ${(markets.reduce((sum, m) => sum + parseFloat(m.volume24h || 0), 0) / 1000000000).toFixed(2)}B
                        </div>
                    </div>
                    <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
                        <div className="text-gray-400 text-sm mb-1">Top Gainer</div>
                        <div className="text-2xl font-bold text-[#0ecb81]">
                            {markets.length > 0 ? `+${Math.max(...markets.map(m => parseFloat(m.priceChange24h || 0))).toFixed(2)}%` : '-'}
                        </div>
                    </div>
                    <div className="bg-[#181a20] rounded-xl border border-gray-800 p-4">
                        <div className="text-gray-400 text-sm mb-1">Top Loser</div>
                        <div className="text-2xl font-bold text-[#f6465d]">
                            {markets.length > 0 ? `${Math.min(...markets.map(m => parseFloat(m.priceChange24h || 0))).toFixed(2)}%` : '-'}
                        </div>
                    </div>
                </div>

                <div className="bg-[#181a20] rounded-xl border border-gray-800 p-1 mb-6 inline-flex overflow-x-auto">
                    {['all', 'gainers', 'losers', 'volume'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition whitespace-nowrap ${activeTab === tab
                                ? 'bg-[#fcd535] text-black shadow-lg'
                                : 'text-gray-400 hover:text-white'
                                }`}
                        >
                            {tab === 'all' ? 'All Markets' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                    ))}
                </div>

                <div className="bg-[#181a20] rounded-2xl border border-gray-800 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-20 text-gray-500">Loading markets...</div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1200px]">
                                    <thead>
                                        <tr className="text-left text-gray-400 text-sm border-b border-gray-800">
                                            <th className="px-6 py-4 font-medium w-12">#</th>
                                            <th className="px-6 py-4 font-medium w-48">Name</th>
                                            <th className="px-6 py-4 font-medium text-right w-32">Price</th>
                                            <th className="px-6 py-4 font-medium text-right w-28">24h Change</th>
                                            <th className="px-6 py-4 font-medium text-right w-32">24h Volume</th>
                                            <th className="px-6 py-4 font-medium text-right w-32">Market Cap</th>
                                            <th className="px-6 py-4 font-medium text-center w-32">Last 7 Days</th>
                                            <th className="px-6 py-4 font-medium text-right w-24">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800">
                                        {paginatedMarkets.map((market, index) => {
                                            const sparkline = getSparklineForSymbol(market.symbol);
                                            return (
                                                <tr key={market.id} className="hover:bg-[#2b3139] transition group">
                                                    <td className="px-6 py-4 text-gray-500 text-sm">
                                                        {(currentPage - 1) * itemsPerPage + index + 1}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <button className="text-gray-500 hover:text-[#fcd535] transition">
                                                                <Star size={16} />
                                                            </button>
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                    src={`https://assets.coincap.io/assets/icons/${market.baseToken?.symbol.toLowerCase()}@2x.png`}
                                                                    alt={market.baseToken?.symbol}
                                                                    className="w-8 h-8 rounded-full"
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = `https://ui-avatars.com/api/?name=${market.baseToken?.symbol}&background=random&size=32`;
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="font-bold text-white text-sm">{market.baseToken?.symbol}</div>
                                                                    <div className="text-xs text-gray-500">{market.baseToken?.name}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="font-bold text-white">
                                                            ${parseFloat(market.lastPrice).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: market.priceDecimals || 8
                                                            })}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className={`flex items-center justify-end gap-1 font-bold text-sm ${parseFloat(market.priceChange24h) >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'
                                                            }`}>
                                                            {parseFloat(market.priceChange24h) >= 0 ? '+' : ''}
                                                            {parseFloat(market.priceChange24h).toFixed(2)}%
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="text-white font-medium text-sm">
                                                            ${(parseFloat(market.volume24h) / 1000000).toFixed(2)}M
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-gray-300 text-sm">
                                                        ${(parseFloat(market.lastPrice) * 19000000).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-center">
                                                            {sparkline ? (
                                                                <Sparkline
                                                                    data={sparkline}
                                                                    color={parseFloat(market.priceChange24h) >= 0 ? '#0ecb81' : '#f6465d'}
                                                                    width={100}
                                                                    height={40}
                                                                />
                                                            ) : (
                                                                <div className="text-xs text-gray-600">Loading...</div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <Link
                                                            to={`/trade/${market.symbol}`}
                                                            className="text-[#fcd535] hover:text-[#f0b90b] font-medium text-sm"
                                                        >
                                                            Trade
                                                        </Link>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="border-t border-gray-800 px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <div className="text-xs sm:text-sm text-gray-400">
                                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredMarkets.length)} of {filteredMarkets.length} markets
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-2 bg-[#0b0e11] border border-gray-700 rounded-lg text-white hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={18} />
                                        </button>

                                        <div className="flex items-center gap-1">
                                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={i}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${currentPage === pageNum
                                                            ? 'bg-[#fcd535] text-black'
                                                            : 'bg-[#0b0e11] border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-2 bg-[#0b0e11] border border-gray-700 rounded-lg text-white hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {filteredMarkets.length === 0 && (
                                <div className="text-center py-20 text-gray-500">
                                    No markets found matching your criteria.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default MarketsPage;
